import { httpsCallable } from 'firebase/functions';
import { functions, firestore } from '../firebase';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, limit } from 'firebase/firestore';

/**
 * Interface for account status data
 */
export interface AccountStatusData {
  ready: boolean;
  details_submitted?: boolean;
  charges_enabled?: boolean;
  payouts_enabled?: boolean;
  setup_link?: string;
  link_expires_at?: number;
}

/**
 * Interface for account data
 */
export interface AccountData {
  documentId: string;
  stripeAccountId: string;
  accountStatus?: AccountStatusData;
}

/**
 * Get connected account from Firestore by vendorId
 * @param vendorId - Vendor ID to look up
 * @returns The account data if found, null otherwise
 */
export const getConnectedAccount = async (vendorId: string): Promise<AccountData | null> => {
  try {
    const accountsRef = collection(firestore, "connectedAccounts");

    const q = query(accountsRef, where("vendorId", "==", vendorId), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      return {
        documentId: doc.id,
        stripeAccountId: data.stripeAccountId,
        accountStatus: data.accountStatus || {}
      };
    }
    
    return null;
  } catch (error) {
    console.error("Error getting connected account:", error);
    throw error;
  }
};

/**
 * Check the status of a Stripe Connect account
 * @param accountId - The Stripe account ID
 * @param docId - The Firestore document ID
 * @returns The account status data
 */
export const checkAccountStatus = async (accountId: string, docId: string): Promise<any> => {
  try {
    // Call Firebase function to check account status
    const checkAccountSetup = httpsCallable(functions, 'checkAccountSetup');
    const result = await checkAccountSetup({ 
      accountId,
      origin: window.location.origin 
    });
    
    const data = result.data as any;
    
    // Update account status in Firestore if there's account data
    if (data.account) {
      const statusData: AccountStatusData = {
        ready: data.ready,
        details_submitted: data.account.details_submitted,
        charges_enabled: data.account.charges_enabled,
        payouts_enabled: data.account.payouts_enabled,
      };
      
      // Only add these fields if they are defined
      if (data.setup_link) statusData.setup_link = data.setup_link;
      if (data.expires_at) statusData.link_expires_at = data.expires_at;
      
      // Update status in Firestore
      const accountRef = doc(firestore, "connectedAccounts", docId);
      await updateDoc(accountRef, {
        'accountStatus': statusData
      });
    }
    
    return data;
  } catch (error) {
    console.error("Error checking account status:", error);
    throw error;
  }
};

/**
 * Create a new Stripe Connect account
 * @param userData - User data for creating account
 * @returns The created account data
 */
export const createStripeAccount = async (userData: { 
  email: string; 
  userId: string;
  vendorId: string;
  country?: string; 
}): Promise<{accountId: string; documentId: string; accountLink?: {url: string; expires_at: number}}> => {
  try {
    // Call Firebase function to create a Stripe Connect account
    const createConnectAccount = httpsCallable(functions, 'createConnectAccount');
    const result = await createConnectAccount({
      email: userData.email,
      userId: userData.userId,
      vendorId: userData.vendorId,
      country: userData.country || 'US',
      origin: window.location.origin
    });
    
    const data = result.data as any;
    
    if (data.error) {
      throw new Error(data.error);
    }    
    
    return {
      accountId: data.accountId,
      documentId: data.documentId,
      accountLink: {
        url: data.setupUrl,
        expires_at: data.expiresAt
      }
    };
  } catch (error) {
    console.error("Error creating Stripe account:", error);
    throw error;
  }
};

/**
 * Refresh an account link when it expires
 * @param accountId - The Stripe account ID
 * @returns The refreshed account link
 */
export const refreshAccountLink = async (accountId: string): Promise<{url: string; expires_at: number}> => {
  try {
    const refreshAccountLinkFn = httpsCallable(functions, 'refreshAccountLink');
    const result = await refreshAccountLinkFn({ 
      accountId,
      origin: window.location.origin
    });
    const data = result.data as any;
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    return {
      url: data.setupUrl,
      expires_at: data.expiresAt
    };
  } catch (error) {
    console.error("Error refreshing account link:", error);
    throw error;
  }
};

/**
 * Create an invoice using Stripe
 * @param invoiceData - The invoice data
 * @returns Success status and invoice ID
 */
export const createInvoice = async (invoiceData: {
  email: string;
  connectedAccountId: string;
  items: Array<{
    amount: number;
    currency: string;
    quantity: number;
    description: string;
  }>;
  daysUntilDue?: number;
  description?: string;
  bidId?: string;
}): Promise<{success: boolean; invoiceId?: string; error?: string}> => {
  try {
    const createInvoiceFn = httpsCallable(functions, 'createInvoice');
    const result = await createInvoiceFn(invoiceData);
    const data = result.data as any;
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    return {
      success: true,
      invoiceId: data.invoiceId
    };
  } catch (error) {
    console.error("Error creating invoice:", error);
    const err = error as Error;
    return {
      success: false,
      error: err.message
    };
  }
}; 